import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PATHS, countries, useLang, useT } from "../lib/i18n";
import { track } from "../lib/tracking";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { NoIndex } from "../components/site/Seo";
import { Logo } from "../components/site/Layout";

const Card = ({ title, children }) => (
  <div className="mx-auto max-w-md px-4 py-16">
    <div className="surface p-8">
      <Logo />
      <h1 className="mt-6 font-display text-4xl font-extrabold">{title}</h1>
      {children}
    </div>
  </div>
);

const Field = ({ label, ...p }) => (<div className="space-y-1.5"><Label>{label}</Label><Input className="bg-secondary" {...p} /></div>);
const Check = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-3 text-sm text-muted-foreground"><Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={testid} /><span>{children}</span></label>
);

export function LoginPage() {
  const t = useT();
  const lang = useLang();
  const nav = useNavigate();
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      login(data.token, data.user);
      nav(data.user.role === "admin" ? "/admin" : PATHS[lang].account);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Card title={t("Anmelden", "Login")}>
      <NoIndex title="Login" />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
        <Field label="E-Mail" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" />
        <Field label={t("Passwort", "Password")} type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" />
        <Button className="w-full rounded-full" disabled={busy} data-testid="login-submit">{t("Anmelden", "Login")}</Button>
      </form>
      <div className="mt-5 flex justify-between text-sm text-muted-foreground">
        <Link to={`/${lang}/forgot-password`} data-testid="login-forgot-link">{t("Passwort vergessen?", "Forgot password?")}</Link>
        <Link to={PATHS[lang].register} className="text-[var(--ball)]" data-testid="login-register-link">{t("Registrieren", "Register")}</Link>
      </div>
    </Card>
  );
}

export function RegisterPage() {
  const t = useT();
  const lang = useLang();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "", name: "", country: "", lang, adult: false, marketing_email: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  useEffect(() => { api.get("/geo").then((r) => r.data.country && setF((x) => ({ ...x, country: x.country || r.data.country }))).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.adult) { toast.error(t("Bitte bestätige, dass du mindestens 18 Jahre alt bist.", "Please confirm you are at least 18.")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", f);
      track("sign_up", { method: "email", country: f.country });
      login(data.token, data.user);
      toast.success(t("Willkommen! Bitte bestätige deine E-Mail.", "Welcome! Please confirm your email."));
      nav(sp.get("next") === "vip" ? PATHS[lang].vip : `${PATHS[lang].account}?welcome=1`);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Card title={t("Kostenlos registrieren", "Create free account")}>
      <NoIndex title="Register" />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
        <Field label="Name" value={f.name} onChange={set("name")} data-testid="register-name" />
        <Field label="E-Mail" type="email" required value={f.email} onChange={set("email")} data-testid="register-email" />
        <Field label={t("Passwort (mind. 8 Zeichen)", "Password (min. 8 characters)")} type="password" minLength={8} required value={f.password} onChange={set("password")} data-testid="register-password" />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{t("Land", "Country")}</Label>
            <select value={f.country} onChange={set("country")} required className="h-10 w-full rounded-md border border-input bg-secondary px-2 text-sm" data-testid="register-country">
              <option value="">–</option>{countries(lang).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>{t("Sprache", "Language")}</Label>
            <select value={f.lang} onChange={set("lang")} className="h-10 w-full rounded-md border border-input bg-secondary px-2 text-sm" data-testid="register-lang"><option value="de">Deutsch</option><option value="en">English</option></select></div>
        </div>
        <Check checked={f.adult} onChange={set("adult")} testid="register-adult">{t("Ich bin mindestens 18 Jahre alt und akzeptiere die AGB und Datenschutzerklärung.", "I am at least 18 years old and accept the terms and privacy policy.")} *</Check>
        <Check checked={f.marketing_email} onChange={set("marketing_email")} testid="register-marketing-email">{t("Newsletter & Angebote per E-Mail erhalten (jederzeit abbestellbar).", "Receive newsletter & offers by email (unsubscribe anytime).")}</Check>
        <Button className="w-full rounded-full" disabled={busy} data-testid="register-submit">{t("Konto erstellen", "Create account")}</Button>
      </form>
      <p className="mt-5 text-sm text-muted-foreground">{t("Schon registriert?", "Already registered?")} <Link to={PATHS[lang].login} className="text-[var(--ball)]">{t("Anmelden", "Login")}</Link></p>
    </Card>
  );
}

export function ForgotPage() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot", { email }); setDone(true); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <Card title={t("Passwort vergessen", "Forgot password")}>
      <NoIndex title="Forgot" />
      {done ? <p className="mt-6 text-muted-foreground" data-testid="forgot-done">{t("Falls ein Konto existiert, erhältst du eine E-Mail.", "If an account exists, you will receive an email.")}</p> : (
        <form onSubmit={submit} className="mt-6 space-y-4"><Field label="E-Mail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /><Button className="w-full rounded-full" data-testid="forgot-submit">{t("Link senden", "Send link")}</Button></form>
      )}
    </Card>
  );
}

function useLinkToken() {
  const [tok] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  useEffect(() => { window.history.replaceState(null, "", window.location.pathname); }, []);
  return tok;
}

export function ResetPage() {
  const t = useT();
  const lang = useLang();
  const tok = useLinkToken();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset", { token: tok, password: pw }); toast.success("OK"); nav(PATHS[lang].login); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Card title={t("Neues Passwort", "New password")}>
      <NoIndex title="Reset" />
      <form onSubmit={submit} className="mt-6 space-y-4"><Field label={t("Neues Passwort", "New password")} type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /><Button className="w-full rounded-full" data-testid="reset-submit">{t("Speichern", "Save")}</Button></form>
    </Card>
  );
}

export function VerifyEmailPage() {
  const t = useT();
  const lang = useLang();
  const tok = useLinkToken();
  const { refresh } = useAuth();
  const [st, setSt] = useState("loading");
  useEffect(() => {
    api.post("/auth/verify-email", { token: tok }).then(() => { setSt("ok"); refresh(); }).catch(() => setSt("err"));
  }, [tok, refresh]);
  return (
    <Card title={t("E-Mail Bestätigung", "Email confirmation")}>
      <NoIndex title="Verify" />
      <p className="mt-6 text-muted-foreground" data-testid="verify-status">{st === "loading" ? "…" : st === "ok" ? t("Deine E-Mail-Adresse ist bestätigt.", "Your email address is confirmed.") : t("Link ungültig oder abgelaufen.", "Link invalid or expired.")}</p>
      <Link to={PATHS[lang].account}><Button className="mt-6 rounded-full">{t("Zum Konto", "Go to account")}</Button></Link>
    </Card>
  );
}
